import React, {
  createContext,
  ReactNode,
  useContext,
  useMemo,
  useReducer,
} from 'react';

export const RECON_PRODUCTS = [
  { value: 'ewa', label: 'EWA' },
  { value: 'savings', label: 'Savings' },
  { value: 'jemMobile', label: 'JemMobile' },
] as const;

export type ReconProductValue = (typeof RECON_PRODUCTS)[number]['value'];
export type RepeatFrequency = 'weekly' | 'monthly';
export type MonthlyType = 'byDateInMonth' | 'nthDayInMonth';
export type InvoiceStrategy =
  | 'combined'
  | 'separate'
  | 'perProduct'
  | 'custom';

export interface ReconGroup {
  id: string;
  products: ReconProductValue[];
}

export interface Schedule {
  repeatFrequency: RepeatFrequency | null;
  interval: number | null;
  dayOfWeek: string | null;
  monthlyType: MonthlyType;
  dateInMonth: number | null;
  nthDayInMonth: number | null;
  posDayInMonth: string | null;
  startDate: Date | null;
}

// A pay cycle the user has picked, carrying its frequency so the calendar
// details can be derived from the selection itself rather than from the
// (purely cosmetic) type filter.
export interface SelectedPayCycle {
  id: number;
  frequency: string;
}

export interface MultiProductReconState {
  // Display filter for the pay cycle list only. It narrows the options shown
  // and never alters the selection - one recon schedule may cover pay cycles
  // of several frequencies.
  payCycleFrequencyFilter: string | null;
  employerPayCycles: SelectedPayCycle[];
  recons: ReconGroup[];
  invoiceStrategy: InvoiceStrategy;
  // Shared across all recons in this event - recon emails go to one list.
  attendees: string[];
  // Calendar event title + description for the whole multi-product recon.
  // Auto-populated from products + pay cycle frequency + employer until the
  // user edits.
  title: string;
  description: string;
  titleManuallyEdited: boolean;
  descriptionManuallyEdited: boolean;
  // One schedule (recurrence + start date) for the whole multi-product recon.
  schedule: Schedule;
  // Custom-strategy only: product -> invoice group number. Missing entries
  // default to 1. Recons are irrelevant here; products in the same group
  // share an invoice regardless of which recon they belong to.
  customInvoiceGroupByProduct: Partial<Record<ReconProductValue, number>>;
  xeroContactId: string | null;
  xeroContactName: string | null;
  invoiceReference: string;
}

const generateReconId = () =>
  `recon-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const createNewRecon = (id: string = generateReconId()): ReconGroup => ({
  id,
  products: [],
});

const initialSchedule: Schedule = {
  repeatFrequency: null,
  interval: null,
  dayOfWeek: null,
  monthlyType: 'byDateInMonth',
  dateInMonth: null,
  nthDayInMonth: null,
  posDayInMonth: null,
  startDate: null,
};

const initialState: MultiProductReconState = {
  payCycleFrequencyFilter: null,
  employerPayCycles: [],
  recons: [createNewRecon()],
  invoiceStrategy: 'separate',
  attendees: [],
  title: '',
  description: '',
  titleManuallyEdited: false,
  descriptionManuallyEdited: false,
  schedule: initialSchedule,
  customInvoiceGroupByProduct: {},
  xeroContactId: null,
  xeroContactName: null,
  invoiceReference: '',
};

const collectAllProducts = (recons: ReconGroup[]): ReconProductValue[] => {
  const seen = new Set<ReconProductValue>();
  recons.forEach((r) => r.products.forEach((p) => seen.add(p)));
  return RECON_PRODUCTS.filter((rp) => seen.has(rp.value)).map((rp) => rp.value);
};

const formatProductList = (products: ReconProductValue[]): string => {
  const labels = products.map(
    (p) => RECON_PRODUCTS.find((rp) => rp.value === p)?.label ?? p,
  );
  if (labels.length === 0) return '';
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
};

// The frequency all selected pay cycles share, or null when the selection is
// empty or spans more than one frequency. Only a unanimous frequency is
// meaningful enough to name in the calendar event.
export const sharedPayCycleFrequency = (
  payCycles: SelectedPayCycle[],
): string | null => {
  const frequencies = new Set(payCycles.map((pC) => pC.frequency));
  if (frequencies.size !== 1) return null;
  return Array.from(frequencies)[0];
};

// "Acme monthly Jem HR EWA Recon", or "Acme Jem HR EWA Recon" when the
// selected pay cycles span several frequencies. Callers pass the result of
// sharedPayCycleFrequency as payCycleType.
export const computeAutoCalendarTitle = (
  employerName: string,
  payCycleType: string | null,
  recons: ReconGroup[],
): string => {
  const products = collectAllProducts(recons);
  if (!employerName || products.length === 0) return '';
  return [
    employerName,
    payCycleType?.toLowerCase(),
    'Jem HR',
    formatProductList(products),
    'Recon',
  ]
    .filter(Boolean)
    .join(' ');
};

export const computeAutoCalendarDescription = (
  employerName: string,
  payCycleType: string | null,
  recons: ReconGroup[],
): string => {
  const products = collectAllProducts(recons);
  if (!employerName || products.length === 0) return '';
  return [
    employerName,
    payCycleType?.toLowerCase(),
    'Jem HR Recons for',
    formatProductList(products),
    'benefits',
  ]
    .filter(Boolean)
    .join(' ');
};

type Action =
  | { type: 'SET_PAY_CYCLE_FREQUENCY_FILTER'; payCycleFrequencyFilter: string | null }
  | { type: 'SET_EMPLOYER_PAY_CYCLES'; employerPayCycles: SelectedPayCycle[] }
  | { type: 'ADD_RECON'; id: string }
  | { type: 'REMOVE_RECON'; id: string }
  | { type: 'UPDATE_RECON'; id: string; updates: Partial<ReconGroup> }
  | { type: 'SET_INVOICE_STRATEGY'; invoiceStrategy: InvoiceStrategy }
  | { type: 'SET_ATTENDEES'; attendees: string[] }
  | { type: 'SET_TITLE'; title: string }
  | { type: 'SET_DESCRIPTION'; description: string }
  | { type: 'SYNC_AUTO_CALENDAR_DETAILS'; employerName: string }
  | { type: 'UPDATE_SCHEDULE'; updates: Partial<Schedule> }
  | {
      type: 'SET_PRODUCT_INVOICE_GROUP';
      product: ReconProductValue;
      groupNumber: number;
    }
  | { type: 'SET_XERO_CONTACT'; contactId: string | null; contactName: string | null }
  | { type: 'SET_INVOICE_REFERENCE'; invoiceReference: string };

const reducer = (
  state: MultiProductReconState,
  action: Action,
): MultiProductReconState => {
  switch (action.type) {
    case 'SET_PAY_CYCLE_FREQUENCY_FILTER':
      // Filtering the visible options must never discard the selection -
      // switching types is how the user builds a mixed-frequency schedule.
      return {
        ...state,
        payCycleFrequencyFilter: action.payCycleFrequencyFilter,
      };
    case 'SET_EMPLOYER_PAY_CYCLES':
      return { ...state, employerPayCycles: action.employerPayCycles };
    case 'ADD_RECON':
      return {
        ...state,
        recons: [...state.recons, createNewRecon(action.id)],
      };
    case 'REMOVE_RECON':
      return {
        ...state,
        recons: state.recons.filter((r) => r.id !== action.id),
      };
    case 'UPDATE_RECON':
      return {
        ...state,
        recons: state.recons.map((r) =>
          r.id === action.id ? { ...r, ...action.updates } : r,
        ),
      };
    case 'SET_PRODUCT_INVOICE_GROUP':
      return {
        ...state,
        customInvoiceGroupByProduct: {
          ...state.customInvoiceGroupByProduct,
          [action.product]: action.groupNumber,
        },
      };
    case 'UPDATE_SCHEDULE': {
      const next: Schedule = { ...state.schedule, ...action.updates };
      if (
        action.updates.repeatFrequency !== undefined &&
        action.updates.repeatFrequency !== state.schedule.repeatFrequency
      ) {
        if (action.updates.repeatFrequency === 'monthly') {
          next.interval = null;
          next.dayOfWeek = null;
        }
      }
      return { ...state, schedule: next };
    }
    case 'SET_XERO_CONTACT':
      return {
        ...state,
        xeroContactId: action.contactId,
        xeroContactName: action.contactName,
      };
    case 'SET_INVOICE_REFERENCE':
      return { ...state, invoiceReference: action.invoiceReference };
    case 'SET_INVOICE_STRATEGY':
      return { ...state, invoiceStrategy: action.invoiceStrategy };
    case 'SET_ATTENDEES':
      return { ...state, attendees: action.attendees };
    case 'SET_TITLE':
      return {
        ...state,
        title: action.title,
        titleManuallyEdited: action.title.trim().length > 0,
      };
    case 'SET_DESCRIPTION':
      return {
        ...state,
        description: action.description,
        descriptionManuallyEdited: action.description.trim().length > 0,
      };
    case 'SYNC_AUTO_CALENDAR_DETAILS': {
      const frequency = sharedPayCycleFrequency(state.employerPayCycles);
      return {
        ...state,
        title: state.titleManuallyEdited
          ? state.title
          : computeAutoCalendarTitle(
              action.employerName,
              frequency,
              state.recons,
            ),
        description: state.descriptionManuallyEdited
          ? state.description
          : computeAutoCalendarDescription(
              action.employerName,
              frequency,
              state.recons,
            ),
      };
    }
    default:
      return state;
  }
};

export const isReconValid = (r: ReconGroup): boolean => r.products.length > 0;

const isWeeklyScheduleValid = (s: Schedule): boolean => {
  if (s.interval === null || s.interval <= 0 || s.interval > 4) return false;
  if (s.interval <= 2 && !s.dayOfWeek) return false;
  return true;
};

const isMonthlyScheduleValid = (s: Schedule): boolean => {
  if (s.monthlyType === 'byDateInMonth' && s.dateInMonth === null) return false;
  if (s.monthlyType === 'nthDayInMonth') {
    if (s.nthDayInMonth === null) return false;
    if (!s.posDayInMonth) return false;
  }
  return true;
};

export const isScheduleValid = (s: Schedule): boolean => {
  if (!s.repeatFrequency) return false;
  if (s.startDate === null) return false;
  if (s.repeatFrequency === 'weekly') return isWeeklyScheduleValid(s);
  if (s.repeatFrequency === 'monthly') return isMonthlyScheduleValid(s);
  return true;
};

const computeIsFormValid = (s: MultiProductReconState): boolean => {
  // payCycleFrequencyFilter is deliberately not checked - it only filters the
  // options list, so a valid schedule can be built without ever setting it.
  if (s.employerPayCycles.length === 0) return false;
  if (s.recons.length === 0) return false;
  if (s.attendees.length === 0) return false;
  if (s.title.trim().length === 0) return false;
  if (s.description.trim().length === 0) return false;
  if (!isScheduleValid(s.schedule)) return false;
  if (!s.xeroContactId) return false;
  return s.recons.every(isReconValid);
};

interface MultiProductReconContextValue {
  state: MultiProductReconState;
  isFormValid: boolean;
  setPayCycleFrequencyFilter: (value: string | null) => void;
  setEmployerPayCycles: (payCycles: SelectedPayCycle[]) => void;
  addRecon: () => string;
  removeRecon: (id: string) => void;
  updateRecon: (id: string, updates: Partial<ReconGroup>) => void;
  setInvoiceStrategy: (value: InvoiceStrategy) => void;
  setAttendees: (attendees: string[]) => void;
  setTitle: (title: string) => void;
  setDescription: (description: string) => void;
  syncAutoCalendarDetails: (employerName: string) => void;
  updateSchedule: (updates: Partial<Schedule>) => void;
  setProductInvoiceGroup: (
    product: ReconProductValue,
    groupNumber: number,
  ) => void;
  setXeroContact: (contactId: string | null, contactName: string | null) => void;
  setInvoiceReference: (value: string) => void;
}

const MultiProductReconContext = createContext<MultiProductReconContextValue | null>(
  null,
);

export const MultiProductReconProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const value = useMemo<MultiProductReconContextValue>(
    () => ({
      state,
      isFormValid: computeIsFormValid(state),
      setPayCycleFrequencyFilter: (payCycleFrequencyFilter) =>
        dispatch({ type: 'SET_PAY_CYCLE_FREQUENCY_FILTER', payCycleFrequencyFilter }),
      setEmployerPayCycles: (employerPayCycles) =>
        dispatch({ type: 'SET_EMPLOYER_PAY_CYCLES', employerPayCycles }),
      addRecon: () => {
        const id = generateReconId();
        dispatch({ type: 'ADD_RECON', id });
        return id;
      },
      removeRecon: (id) => dispatch({ type: 'REMOVE_RECON', id }),
      updateRecon: (id, updates) =>
        dispatch({ type: 'UPDATE_RECON', id, updates }),
      setInvoiceStrategy: (invoiceStrategy) =>
        dispatch({ type: 'SET_INVOICE_STRATEGY', invoiceStrategy }),
      setAttendees: (attendees) =>
        dispatch({ type: 'SET_ATTENDEES', attendees }),
      setTitle: (title) => dispatch({ type: 'SET_TITLE', title }),
      setDescription: (description) =>
        dispatch({ type: 'SET_DESCRIPTION', description }),
      syncAutoCalendarDetails: (employerName) =>
        dispatch({ type: 'SYNC_AUTO_CALENDAR_DETAILS', employerName }),
      updateSchedule: (updates) =>
        dispatch({ type: 'UPDATE_SCHEDULE', updates }),
      setProductInvoiceGroup: (product, groupNumber) =>
        dispatch({ type: 'SET_PRODUCT_INVOICE_GROUP', product, groupNumber }),
      setXeroContact: (contactId, contactName) =>
        dispatch({ type: 'SET_XERO_CONTACT', contactId, contactName }),
      setInvoiceReference: (invoiceReference) =>
        dispatch({ type: 'SET_INVOICE_REFERENCE', invoiceReference }),
    }),
    [state],
  );

  return (
    <MultiProductReconContext.Provider value={value}>
      {children}
    </MultiProductReconContext.Provider>
  );
};

export const useMultiProductRecon = () => {
  const ctx = useContext(MultiProductReconContext);
  if (!ctx) {
    throw new Error(
      'useMultiProductRecon must be used inside MultiProductReconProvider',
    );
  }
  return ctx;
};

export interface ResolvedInvoice {
  groupNumber: number;
  // Recons whose products contribute to this invoice. Multiple recons can
  // contribute to one invoice (combined / custom) and one recon can split
  // across multiple invoices (perProduct / custom by product).
  reconIndexes: number[];
  // Products included on this invoice, in canonical RECON_PRODUCTS order.
  products: ReconProductValue[];
}

export const productLabels = (products: ReconProductValue[]): string =>
  products
    .map((p) => RECON_PRODUCTS.find((rp) => rp.value === p)?.label ?? p)
    .join(', ');

// "Recon 2 (EWA, Savings)" - falls back to "Recon 2" while no products
// are selected yet.
export const reconLabelWithProducts = (
  recon: ReconGroup,
  index: number,
): string => {
  const label = `Recon ${index + 1}`;
  if (recon.products.length === 0) return label;
  return `${label} (${productLabels(recon.products)})`;
};

const reconIndexesForProducts = (
  recons: ReconGroup[],
  products: ReconProductValue[],
): number[] =>
  recons
    .map((r, i) => (r.products.some((p) => products.includes(p)) ? i : -1))
    .filter((i) => i >= 0);

export const computeInvoiceGroups = (
  recons: ReconGroup[],
  strategy: InvoiceStrategy,
  customInvoiceGroupByProduct: Partial<Record<ReconProductValue, number>> = {},
): ResolvedInvoice[] => {
  if (recons.length === 0) return [];

  if (strategy === 'combined') {
    return [
      {
        groupNumber: 1,
        reconIndexes: recons.map((_, i) => i),
        products: collectAllProducts(recons),
      },
    ];
  }

  if (strategy === 'separate') {
    return recons.map((r, i) => ({
      groupNumber: i + 1,
      reconIndexes: [i],
      products: RECON_PRODUCTS.filter((rp) => r.products.includes(rp.value)).map(
        (rp) => rp.value,
      ),
    }));
  }

  if (strategy === 'perProduct') {
    // One invoice per selected product, even when products share a recon.
    const invoices: ResolvedInvoice[] = [];
    recons.forEach((r, reconIndex) => {
      RECON_PRODUCTS.forEach((rp) => {
        if (!r.products.includes(rp.value)) return;
        invoices.push({
          groupNumber: invoices.length + 1,
          reconIndexes: [reconIndex],
          products: [rp.value],
        });
      });
    });
    return invoices;
  }

  // custom - group SELECTED products by their assigned invoice group.
  // Recon assignment is incidental and derived from each group's products.
  const selectedProducts = collectAllProducts(recons);
  const byGroup = new Map<number, ReconProductValue[]>();
  selectedProducts.forEach((p) => {
    const group = customInvoiceGroupByProduct[p] ?? 1;
    const existing = byGroup.get(group) ?? [];
    byGroup.set(group, [...existing, p]);
  });
  const sorted = Array.from(byGroup.entries()).sort((a, b) => a[0] - b[0]);
  return sorted.map(([, products], idx) => ({
    groupNumber: idx + 1,
    reconIndexes: reconIndexesForProducts(recons, products),
    products,
  }));
};

// Human label for one resolved invoice line. Strategy-aware:
//   perProduct: "EWA (Recon 1)"
//   custom:     "EWA, Savings" (product-centric since the user grouped products)
//   separate/combined: "Recon 1 (EWA, Savings) + Recon 2 (...)" (recon-centric)
export const invoiceGroupLabel = (
  invoice: ResolvedInvoice,
  recons: ReconGroup[],
  strategy: InvoiceStrategy,
): string => {
  if (strategy === 'perProduct') {
    return `${productLabels(invoice.products)} (Recon ${
      invoice.reconIndexes[0] + 1
    })`;
  }
  if (strategy === 'custom') {
    return productLabels(invoice.products);
  }
  return invoice.reconIndexes
    .map((i) => reconLabelWithProducts(recons[i], i))
    .join(' + ');
};

export const useReconGroup = (id: string) => {
  const { state, updateRecon } = useMultiProductRecon();
  const recon = state.recons.find((r) => r.id === id);
  if (!recon) {
    throw new Error(`useReconGroup: no recon group with id ${id}`);
  }
  return {
    recon,
    updateRecon: (updates: Partial<ReconGroup>) => updateRecon(id, updates),
  };
};
